import React, { useEffect, useState } from 'react';
import './lifeEvents.scss';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { message } from 'antd';
import DOMPurify from 'dompurify';
import { addLifeEventRequest } from '../../core/payment/paymentActions';
import Main from '../../components/layout/Main';
import SEO from '../../components/SEO';
import { ZODIAC_SIGNS, getLifeEventsSEOTitle, getLifeEventsSEODescription, getLifeEventsJsonLd } from '../../components/SEO/seoConfig';
import useLanguage from '../../core/language/useLanguage';
import { getSelectedTranslation, getTranslation } from '../../core/utils/helpers';
import YellowThemeButton from '../../components/YellowThemeButton';
import addLifeEventIcon from '../../assets/images/add-life-event-button.svg';
import removeLifeEventIcon from '../../assets/images/remove-life-event-button.svg';
import { getNewSingleHoroscopeRequest } from '../../core/horoscope/horoscopeActions';
import HoroscopeCarousel from '../../components/HoroscopeCarousel';
import LegalPolicyLinks from '../../components/LegalPolicyLinks';
import Accordion from '../../components/Accordion';
import app from '../../core/constants';

const LifeEvents = () => {
    const { sign } = useParams();
    const [activeYearIndex, setActiveYearIndex] = useState(0);

    // Get sign name for SEO
    const signConfig = ZODIAC_SIGNS.find((s) => s.key === sign?.toLowerCase());
    const signName = signConfig?.name || sign;
    const seoUrl = `/life-events/${sign}`;

    const horoscope = useSelector(state => state.horoscope);
    const payment = useSelector(state => state.payment);
    const user = useSelector(state => state.user);
    const { isLoggedIn } = user;

    const { singleHoroscope } = horoscope;
    const { lifeEvents } = payment;
    const { language } = useLanguage();

    const dispatch = useDispatch();
    const history = useHistory();

    // Get the active year's horoscope data (ensure it's an array)
    const yearlyHoroscopeData = Array.isArray(singleHoroscope?.horoscopeData) ? singleHoroscope.horoscopeData : [];
    const upcomingYearsData = Array.isArray(singleHoroscope?.upcomingYears) ? singleHoroscope.upcomingYears : [];

    // Combine horoscopeData and upcomingYears, sorted by year
    const allYearsData = [...yearlyHoroscopeData, ...upcomingYearsData].sort((a, b) => {
        const yearA = a.year || (a.date ? moment(a.date).year() : 0);
        const yearB = b.year || (b.date ? moment(b.date).year() : 0);
        return yearA - yearB;
    });

    const activeYearData = allYearsData[activeYearIndex];
    const horoscopeParts = activeYearData?.parts || [];

    const isYearLocked = activeYearData?.is_purchasable && !activeYearData?.user_has_purchased;

    const translation = getSelectedTranslation(singleHoroscope?.translations, language?.selectedLanguage?.id);

    const sortedParts = (horoscopeParts && Array.isArray(horoscopeParts) ? [...horoscopeParts] : []).sort((a, b) => {
        if (a.user_has_access && !b.user_has_access) {
            return -1; // a comes before b
        }
        if (!a.user_has_access && b.user_has_access) {
            return 1; // b comes before a
        }
        return 0; // no change in order
    });

    useEffect(() => {
        window.scrollTo(0, 0);
        // Fetch yearly horoscope data for this sign
        dispatch(getNewSingleHoroscopeRequest({ timePeriod: 'yearly', horoscopeSign: sign, userAuthenticated: isLoggedIn }));
    }, [sign, isLoggedIn]);

    // Set active year to current year when data loads
    useEffect(() => {
        if (allYearsData.length > 0) {
            const now = moment();
            const currentYearIndex = allYearsData.findIndex((item) => {
                const itemYear = item.year || (item.date ? moment(item.date).year() : 0);
                return itemYear === now.year();
            });
            // Use current year if found, otherwise default to first item
            setActiveYearIndex(currentYearIndex >= 0 ? currentYearIndex : 0);
        }
    }, [allYearsData.length]);

    // Build carousel data for year switching
    const getCarouselTitle = (horoscopeData) => {
        // Use year property if available (from upcomingYears), otherwise parse from date
        if (horoscopeData?.year) {
            return String(horoscopeData.year);
        }
        const date = horoscopeData?.date;
        if (!date || !moment(date).isValid()) {
            return 'Year';
        }
        return moment(date).format('YYYY');
    };

    const carouselData = allYearsData.map((item, index) => ({
        id: index + 1,
        onClick: () => setActiveYearIndex(index),
        title: getCarouselTitle(item),
    }));

    useEffect(() => {
        // check to see if basket requires emptying
        if (lifeEvents?.length > 0 && horoscopeParts && Array.isArray(horoscopeParts)) {
            // if item in the basket is already owned, reset basket
            for (let i = 0; i < lifeEvents.length; i++) {
                const eventId = lifeEvents[i];
                const foundItem = horoscopeParts.find(part => part.id === eventId);
                if (foundItem && foundItem.user_has_access) {
                    dispatch(addLifeEventRequest('reset'));
                    break;
                }
            }
            // empty basket if items are from another horoscope
            const foundFirstBasketItem = horoscopeParts.find((part) => part.id === lifeEvents[0]);
            if (!foundFirstBasketItem) {
                dispatch(addLifeEventRequest('reset'));
            }
        }
    }, [horoscopeParts, lifeEvents]);

    const handleBasketClick = (part) => {
        dispatch(addLifeEventRequest(part?.id));
    };

    const getBasketTotal = () => {
        const basketTotal = (horoscopeParts ?? [])
            .filter(part => lifeEvents?.includes(part?.id))
            .reduce((accumulator, part) => accumulator + (part?.current_price ?? 0), 0);
        return basketTotal.toFixed(2);
    };

    const handleBuyNow = () => {
        history.push({ pathname: '/enter-payment-details', state: { type: 'lifeEvent' } });
    };

    // Handle purchase of locked yearly horoscope - redirect to payment page
    const handlePurchaseYear = () => {
        if (!isLoggedIn) {
            history.push({ pathname: '/signup', state: { redirect: `/life-events/${sign}` } });
            return;
        }

        if (!activeYearData?.id) {
            message.error('Unable to process purchase - no horoscope ID found');
            return;
        }

        // Redirect to payment page with yearly horoscope purchase info
        history.push({
            pathname: '/enter-payment-details',
            state: {
                type: 'yearlyHoroscope',
                horoscopeDataId: activeYearData.id,
                year: activeYearData.year || moment(activeYearData.date).format('YYYY'),
                sign,
            },
        });
    };

    const handleViewClick = (part) => {
        const signKey = singleHoroscope?.key?.toLowerCase();
        // Extract year from active year data (use year property or parse from date)
        const year = activeYearData?.year || (activeYearData?.date ? new Date(activeYearData.date).getFullYear() : null);
        const partSlug = part?.slug;

        if (signKey && year && partSlug) {
            history.push(`/horoscope/yearly/${signKey}/${year}/${partSlug}`);
        }
    };

    const handleSignUpClick = () => {
        history.push({ pathname: '/horoscope-generated', state: { hideModal: true } });
    };

    const getPaddingBottom = () => {
        if (lifeEvents?.length > 0 && window.innerWidth < 576) {
            return '280px';
        }
        return '0px';
    };

    const renderLifeEventItem = (part) => {
        const partTranslation = getSelectedTranslation(part?.translations, language?.selectedLanguage?.id);
        const isInBasket = lifeEvents?.find((id) => id === part?.id);
        const userHasAccess = app.ENABLE_FREE_PREMIUM || part?.user_has_access;
        let liClassName = '';

        if (userHasAccess) {
            liClassName = 'activated-part';
        } else if (isInBasket) {
            liClassName = 'selected-part';
        }

        return (
            <li key={part.id} className={liClassName}>
                {!userHasAccess && (
                    <div className="svg-wrapper" onClick={() => handleBasketClick(part)}>
                        <img src={isInBasket ? removeLifeEventIcon : addLifeEventIcon} alt="add-life-event" />
                    </div>
                )}
                <div className="list-item-content">
                    <div className="part-title">
                        <h3>{partTranslation?.name}</h3>
                        {!userHasAccess && <span>£{part?.current_price}</span>}
                    </div>
                    <p>{partTranslation?.short_description || ''}</p>
                    {userHasAccess && (
                        <div className="view-event-btn">
                            <YellowThemeButton onClick={() => handleViewClick(part)}>{getTranslation(language, 'view', 'View')}</YellowThemeButton>
                        </div>
                    )}
                </div>
            </li>
        );
    };

    const renderBasketItem = (partId) => {
        const basketPart = horoscopeParts?.find((part) => part?.id === partId);
        const partTranslation = getSelectedTranslation(basketPart?.translations, language?.selectedLanguage?.id);
        return (<li key={partId} className="basket-item">{partTranslation?.name}</li>);
    };

    // Get special forecasts from active year data
    // activeYearData is an item from horoscopeData[] which has specialForecasts directly
    const specialForecasts = activeYearData?.specialForecasts || activeYearData?.special_forecasts || [];

    // Helper function to validate YouTube video code
    const isValidYouTubeVideo = (code) => {
        if (!code) return false;
        if (code.length === 9 && /^[0-9]+$/.test(code)) {
            return false;
        }
        if (code.indexOf('/') > -1) {
            return false;
        }
        return true;
    };

    // Render Special Forecasts section
    const renderSpecialForecasts = () => {
        if (!specialForecasts || specialForecasts.length === 0) {
            return null;
        }

        const yearKey = activeYearData?.year || activeYearData?.id || activeYearIndex;
        return (
            <div className="special-forecasts-section" key={`special-forecasts-${yearKey}`}>
                <h2 className="special-forecasts-title">
                    {getTranslation(language, 'special_forecasts', 'Special Forecasts')}
                </h2>
                <div className="special-forecasts-list">
                    {specialForecasts.map((forecast) => {
                        const forecastTranslation = getSelectedTranslation(
                            forecast?.translations,
                            language?.selectedLanguage?.id,
                        );

                        const forecastVideoCode = forecastTranslation?.video_link;
                        const hasVideo = forecastVideoCode && isValidYouTubeVideo(forecastVideoCode);

                        return (
                            <Accordion
                                key={forecast.id}
                                title={forecastTranslation?.name || forecast.name || 'Forecast'}>
                                {hasVideo && (
                                    <div className="accordion__video-block">
                                        <iframe
                                            src={`https://www.youtube.com/embed/${forecastVideoCode}`}
                                            title="YouTube video player"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                            referrerPolicy="strict-origin-when-cross-origin" />
                                    </div>
                                )}
                                {forecastTranslation?.description && (
                                    <div
                                        className="special-forecast-description"
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(forecastTranslation.description),
                                        }} />
                                )}
                            </Accordion>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <>
            <SEO
                title={getLifeEventsSEOTitle(signName)}
                description={getLifeEventsSEODescription(signName)}
                url={seoUrl}
                jsonLd={getLifeEventsJsonLd(signName, seoUrl)} />
            <Main
                className="center-align"
                headerTitle={(
                    <div>
                        <img style={{ height: 70, width: 70 }}
                            src="https://prakashastrologer-19e87.kxcdn.com/wp-content/uploads/2020/05/PrakashAstrologerLogo.svg"
                            alt="logo" />
                    </div>
                )}
                menuWidth={350}
                menuPlacement="right"
                showHeader
                displayRightMenu
                showFooter={false}
                backLink={(
                    <div style={{ display: 'flex' }}>
                        <Link to="/dashboard?tab=3" className="backlink" style={{ marginBottom: '24px' }} />
                    </div>
                )}>
                <div className="purchase-life-events">
                    {/* Year Carousel - only show if multiple years available */}
                    {carouselData.length > 0 && (
                        <div className="year-carousel-wrapper" style={{ marginBottom: '20px' }}>
                            <HoroscopeCarousel data={carouselData} activeSlide={activeYearIndex + 1} />
                        </div>
                    )}
                    <h1>{getTranslation(language, 'purchase_life_events', 'Purchase Life Events')} - {translation?.name} {activeYearData?.year || (activeYearData?.date ? moment(activeYearData.date).format('YYYY') : '')}</h1>
                    <p className="life-events-description">{getTranslation(language, 'life_events_description', 'The premium annual horoscope offers an in-depth exploration of the upcoming year through 13 meticulously detailed sections.')}</p>
                </div>
            </Main>
            <div className="content-container" style={{ paddingBottom: getPaddingBottom() }}>
                <div className={`content-grid ${app.ENABLE_FREE_PREMIUM ? 'no-basket' : ''}`}>
                    <div className="parts-list">
                        {!app.ENABLE_FREE_PREMIUM && !singleHoroscope?.is_user_subscribed && (<YellowThemeButton onClick={handleSignUpClick} style={{ maxWidth: '350px' }}>{getTranslation(language, 'sign_up_to_diamond_package', 'Sing Up to The Diamond Package')}</YellowThemeButton>)}
                        {isYearLocked ? (
                            <div className="locked-year-overlay">
                                <div className="locked-content">
                                    <h3>{getTranslation(language, 'year_locked', 'This Year is Locked')}</h3>
                                    <p>{getTranslation(language, 'purchase_to_unlock', 'Purchase this horoscope to unlock the life events for')} {activeYearData?.year || moment(activeYearData?.date).format('YYYY')}</p>
                                    <YellowThemeButton onClick={handlePurchaseYear}>
                                        {getTranslation(language, 'purchase_now', 'Purchase Now')}
                                    </YellowThemeButton>
                                </div>
                                <ul className="blurred-list">
                                    {sortedParts.map((part) => renderLifeEventItem(part))}
                                </ul>
                            </div>
                        ) : (
                            <ul>
                                {sortedParts.map((part) => renderLifeEventItem(part))}
                            </ul>
                        )}
                    </div>
                    {!app.ENABLE_FREE_PREMIUM && (
                        <div className="basket">
                            <h3>{getTranslation(language, 'basket', 'Basket')}</h3>
                            <div className="basket-info-wrapper">
                                <p>{getTranslation(language, 'quantity', 'Quantity')}</p>
                                <p>{lifeEvents?.length}</p>
                            </div>
                            {lifeEvents?.length > 0
                                ? <ul>{lifeEvents.map((id) => renderBasketItem(id))}</ul>
                                : null}
                            <div className="basket-info-wrapper" style={{ marginBottom: '12px' }}>
                                <p>{getTranslation(language, 'total_cost', 'Total Cost')}</p>
                                <p>£{getBasketTotal()}</p>
                            </div>
                            <YellowThemeButton onClick={handleBuyNow} disabled={!lifeEvents.length}>{getTranslation(language, 'buy_now', 'Buy Now')}</YellowThemeButton>
                        </div>
                    )}
                </div>
            </div>

            {/* Special Forecasts Section - Full width at bottom */}
            {renderSpecialForecasts()}

            {!app.ENABLE_FREE_PREMIUM && (
                <div className="mobile-basket" style={lifeEvents?.length > 0 ? { maxHeight: '500px', bottom: '0px' } : { maxHeight: '0px', bottom: '-50px' }}>
                    <h3>{getTranslation(language, 'basket', 'Basket')}</h3>
                    <div className="basket-info-wrapper">
                        <p>{getTranslation(language, 'quantity', 'Quantity')}</p>
                        <p>{lifeEvents?.length}</p>
                    </div>
                    <div className="basket-info-wrapper" style={{ marginBottom: '12px' }}>
                        <p>{getTranslation(language, 'total_cost', 'Total Cost')}</p>
                        <p>£{getBasketTotal()}</p>
                    </div>
                    <YellowThemeButton onClick={handleBuyNow} disabled={!lifeEvents.length}>{getTranslation(language, 'buy_now', 'Buy Now')}</YellowThemeButton>
                </div>
            )}

            <div style={{ padding: '20px', marginTop: '10px' }}>
                <LegalPolicyLinks
                    layout="grid"
                    showTitle
                    showSeparator
                    fontSize={16} />
            </div>

        </>
    );
};

export default LifeEvents;
